// Pure calculation utilities. All money-returning helpers return Numbers (not strings).
// Inputs are assumed to be non-negative numbers; guard against NaN at the call site.

export const currency = (n: number, code = "USD") => {
  if (!isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

export const num = (n: number, digits = 2) => {
  if (!isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
};

export const safe = (v: string | number): number => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return isFinite(n) && !isNaN(n) ? n : 0;
};

// 1) Credit card interest / finance charge
export function calcInterest(args: {
  balance: number;
  apr: number;
  days: number;
  payment?: number;
  avgDailyBalance?: number;
}) {
  const { balance, apr, days } = args;
  const dpr = apr / 100 / 365;
  const adb =
    args.avgDailyBalance && args.avgDailyBalance > 0
      ? args.avgDailyBalance
      : args.payment && args.payment > 0
      ? Math.max(balance - args.payment / 2, 0)
      : balance;
  const interest = adb * dpr * days;
  return {
    dailyPeriodicRate: dpr,
    averageDailyBalance: adb,
    interest,
    total: balance + interest,
  };
}

// 2) Cash advance
export function calcCashAdvance(args: {
  amount: number;
  feePct: number;
  minFee: number;
  apr: number;
  days: number;
}) {
  const { amount, feePct, minFee, apr, days } = args;
  const pctFee = amount * (feePct / 100);
  const advanceFee = Math.max(pctFee, minFee);
  const interest = amount * (apr / 100 / 365) * days;
  return {
    advanceFee,
    interest,
    total: advanceFee + interest,
    grandTotal: amount + advanceFee + interest,
  };
}

// 3) Balance transfer
export function calcBalanceTransfer(args: {
  amount: number;
  feePct: number;
  introApr: number;
  introMonths: number;
  regularApr: number;
  monthlyPayment: number;
}) {
  const { amount, feePct, introApr, introMonths, regularApr, monthlyPayment } = args;
  const transferFee = amount * (feePct / 100);
  const principalWithFee = amount + transferFee;

  // Simulate pay-down during intro period
  let remaining = principalWithFee;
  let introInterest = 0;
  const monthlyIntroRate = introApr / 100 / 12;
  for (let i = 0; i < introMonths && remaining > 0; i++) {
    const int = remaining * monthlyIntroRate;
    introInterest += int;
    remaining = Math.max(remaining + int - monthlyPayment, 0);
  }
  // Post-intro: estimate months to payoff at regular APR
  let postInterest = 0;
  let months = 0;
  const monthlyRegRate = regularApr / 100 / 12;
  let rem = remaining;
  while (rem > 0 && months < 600) {
    const int = rem * monthlyRegRate;
    postInterest += int;
    rem = rem + int - monthlyPayment;
    months++;
    if (monthlyPayment <= rem * monthlyRegRate) break; // not paying down
  }
  const totalCostTransfer = transferFee + introInterest + postInterest;

  // Comparison: keeping balance on original card at regularApr
  let origRem = amount;
  let origInterest = 0;
  let origMonths = 0;
  while (origRem > 0 && origMonths < 600) {
    const int = origRem * monthlyRegRate;
    origInterest += int;
    origRem = origRem + int - monthlyPayment;
    origMonths++;
    if (monthlyPayment <= origRem * monthlyRegRate) break;
  }

  return {
    transferFee,
    introInterest,
    postIntroInterest: postInterest,
    monthsAfterIntroToPayoff: months,
    totalCostTransfer,
    originalCardInterest: origInterest,
    savings: origInterest - totalCostTransfer,
  };
}

// 4) Foreign transaction fee (generic)
export function calcForeignTx(args: {
  foreignAmount: number;
  exchangeRate: number;
  feePct: number;
  markupPct?: number;
}) {
  const { foreignAmount, exchangeRate, feePct } = args;
  const markupPct = args.markupPct || 0;
  const converted = foreignAmount * exchangeRate;
  const fee = converted * (feePct / 100);
  const markup = converted * (markupPct / 100);
  return {
    converted,
    fee,
    markup,
    total: converted + fee + markup,
  };
}

// 7) Annual fee break-even
export function calcAnnualFee(args: {
  annualFee: number;
  yearlySpend: number;
  rewardsPct: number;
  credits: number;
  perks: number;
  signupBonus: number;
}) {
  const { annualFee, yearlySpend, rewardsPct, credits, perks, signupBonus } = args;
  const rewards = yearlySpend * (rewardsPct / 100);
  const gross = rewards + credits + perks + signupBonus;
  const net = gross - annualFee;
  const breakEvenSpend = rewardsPct > 0 ? Math.max((annualFee - credits - perks) / (rewardsPct / 100), 0) : Infinity;
  return { rewards, gross, net, breakEvenSpend };
}

// 8) Late fee
export function calcLateFee(args: {
  missedAmount: number;
  lateFee: number;
  apr: number;
  daysLate: number;
}) {
  const { missedAmount, lateFee, apr, daysLate } = args;
  const interest = missedAmount * (apr / 100 / 365) * daysLate;
  return { lateFee, interest, total: lateFee + interest };
}

// 9) Percentage / surcharge
export function calcPercentageFee(args: { amount: number; feePct: number }) {
  const { amount, feePct } = args;
  const fee = amount * (feePct / 100);
  return { fee, total: amount + fee };
}

// 10) Monthly finance charge
export function calcFinanceCharge(args: {
  avgDailyBalance: number;
  apr: number;
  days: number;
}) {
  const { avgDailyBalance, apr, days } = args;
  const financeCharge = avgDailyBalance * (apr / 100 / 365) * days;
  return { financeCharge };
}
